// Built-in glyphs (presentation contract section 5.2), drawn in a 24-unit box with currentColor.
// `gi:<name>` draws a bundled game-icons.net icon (presentation-2 contract section 4).
import { GAME_ICONS } from '../icons/gameIcons.ts';
import { COLOR_ICONS, type ColorIconPart } from '../icons/colorIcons.ts';
const PATHS: Record<string, string> = {
  circle: 'M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18z',
  square: 'M4 4h16v16H4z',
  triangle: 'M12 3l10 18H2z',
  diamond: 'M12 2l10 10l-10 10L2 12z',
  star: 'M12 2l2.9 6.6l7.1.6l-5.4 4.7l1.7 7.1L12 17.3L5.7 21l1.7-7.1L2 9.2l7.1-.6z',
  hex: 'M7 3h10l5 9l-5 9H7l-5-9z',
  drop: 'M12 2c4 6 7 9.5 7 13a7 7 0 0 1-14 0c0-3.5 3-7 7-13z',
  leaf: 'M20 4C9 4 4 9 4 16c0 1.5.3 2.8.8 4C6 14 10 10 16 8c-5 3-8 7-9.4 12.3C8 20.8 9.5 21 11 21c6 0 10-6 9-17z',
  stone: 'M5 9l5-5h6l4 6l-1 7l-6 4H8l-5-5z',
  berry: 'M12 8a6.5 6.5 0 1 0 0 13a6.5 6.5 0 1 0 0-13zM12 8c0-3 2-5 5-5c-1 2-2.5 4-5 5z',
  twig: 'M4 20L18 6M10 14l-3-6M14 10l5 1',
  resin: 'M12 3c-3 4-6 7-6 11a6 6 0 0 0 12 0c0-4-3-7-6-11zM9 15a3 3 0 0 0 3 3',
  coin: 'M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18zM12 7v10M9 9.5h4.5a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3H15',
  card: 'M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z',
  worker: 'M12 3a3.5 3.5 0 1 0 0 7a3.5 3.5 0 1 0 0-7zM5 21c0-5 3-8 7-8s7 3 7 8z',
  point: 'M12 2l3 6.5l7 1l-5 5l1.2 7L12 18l-6.2 3.5L7 14.5l-5-5l7-1z',
};
const STROKED = new Set(['twig', 'coin', 'resin']);

export function Glyph({ name, size = 16, label }: { name: string; size?: number; label?: string }) {
  const colored = name.startsWith('svg:') ? COLOR_ICONS[name.slice(4)] : undefined;
  if (colored) return <svg className="glyph" width={size} height={size} viewBox="0 0 24 24" role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
    {colored.map((part, i) => <path key={i} d={part.d} fill={part.fill} stroke={part.stroke} strokeWidth={part.width} strokeLinecap="round" strokeLinejoin="round" />)}
  </svg>;
  if (name.startsWith('gi:') && GAME_ICONS[name.slice(3)]) {
    return (
      <svg className="glyph" width={size} height={size} viewBox="0 0 512 512" role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
        <path d={GAME_ICONS[name.slice(3)]} fill="currentColor" />
      </svg>
    );
  }
  const d = PATHS[name] ?? PATHS.circle!;
  const stroked = STROKED.has(name);
  return (
    <svg className="glyph" width={size} height={size} viewBox="0 0 24 24" role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <path d={d} fill={stroked ? 'none' : 'currentColor'} stroke={stroked ? 'currentColor' : 'none'} strokeWidth={stroked ? 2 : 0} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function hasGlyph(name: string): boolean { return name in PATHS || (name.startsWith('gi:') && name.slice(3) in GAME_ICONS) || (name.startsWith('svg:') && name.slice(4) in COLOR_ICONS); }

/** Path data and box size for a glyph name, for drawing inside another SVG. */
export function glyphPath(name: string): { d: string; box: number; stroked: boolean; parts?: ColorIconPart[] } {
  if (name.startsWith('svg:') && COLOR_ICONS[name.slice(4)]) return { d: '', box: 24, stroked: false, parts: COLOR_ICONS[name.slice(4)] };
  if (name.startsWith('gi:') && GAME_ICONS[name.slice(3)]) return { d: GAME_ICONS[name.slice(3)]!, box: 512, stroked: false };
  return { d: PATHS[name] ?? PATHS.circle!, box: 24, stroked: STROKED.has(name) };
}
